/**
 * Custom hook for Session History data management
 * Handles loading, filtering, pagination, and CRUD operations
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Earnings rates
export const OBSERVER_RATE_PER_MIN = 5; // ₹5 per minute for observer
export const PRINCIPAL_RATE_PER_SESSION = 10; // ₹10 per session for principal

export const useSessionHistory = (navigate, toast) => {
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    observerId: '',
    childId: ''
  });
  const [filterOptions, setFilterOptions] = useState({ observers: [], children: [] });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [principalName, setPrincipalName] = useState('');

  const getToken = useCallback(() => {
    return localStorage.getItem('principal_token');
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      
      const params = { token, page, limit: 50 };
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      if (filters.observerId) params.observer_id = filters.observerId;
      if (filters.childId) params.child_id = filters.childId;
      
      const [historyRes, summaryRes, dashRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/principal/session-history`, { params }),
        axios.get(`${BACKEND_URL}/api/principal/session-summary`, { 
          params: { 
            token, 
            start_date: filters.startDate || undefined,
            end_date: filters.endDate || undefined
          } 
        }),
        axios.get(`${BACKEND_URL}/api/principal/dashboard?token=${token}`).catch(() => ({ data: {} }))
      ]);
      
      setSessions(historyRes.data.sessions || []);
      setTotal(historyRes.data.total || 0);
      setTotalPages(historyRes.data.total_pages || 1);
      setSummary(summaryRes.data);
      setFilterOptions({
        observers: historyRes.data.filter_options?.observers || [],
        children: historyRes.data.filter_options?.children || []
      });
      setPrincipalName(dashRes.data?.principal?.name || 'Principal');
    } catch (error) {
      console.error('Load error:', error);
      toast({ title: "Error", description: "Failed to load sessions", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [page, filters, toast, getToken]);

  // Check auth on mount
  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [navigate, loadData, getToken]);

  const clearFilters = useCallback(() => {
    setFilters({ startDate: '', endDate: '', observerId: '', childId: '' });
    setPage(1);
  }, []);

  // Export sessions as CSV
  const exportSessions = useCallback(async () => {
    setExporting(true);
    try {
      const token = getToken();
      const params = { token };
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
      if (filters.observerId) params.observer_id = filters.observerId;
      if (filters.childId) params.child_id = filters.childId;
      
      const res = await axios.get(`${BACKEND_URL}/api/principal/sessions/export`, { params });
      const exportData = res.data.sessions || [];
      
      if (exportData.length === 0) {
        toast({ title: "No Data", description: "No sessions to export", variant: "destructive" });
        return;
      }
      
      // Convert to CSV
      const headers = ['session_id', 'session_date', 'start_time', 'end_time', 'child_id', 'child_name', 'observer_id', 'observer_name', 'duration_minutes', 'earnings', 'status', 'notes', 'action'];
      const csvRows = [headers.join(',')];
      
      exportData.forEach(row => {
        const values = headers.map(h => {
          const val = row[h] ?? '';
          const strVal = String(val).replace(/"/g, '""');
          return strVal.includes(',') || strVal.includes('"') || strVal.includes('\n') ? `"${strVal}"` : strVal;
        });
        csvRows.push(values.join(','));
      });
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `sessions_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({ title: "Success", description: `Exported ${exportData.length} sessions` });
    } catch (error) {
      console.error('Export error:', error);
      toast({ title: "Error", description: "Failed to export sessions", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  }, [filters, toast, getToken]);

  // Parse CSV line helper
  const parseCSVLine = useCallback((line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        if (inQuotes && line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current);
    return result;
  }, []);

  // Handle file upload (CSV or Excel)
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
    
    setImporting(true);
    try {
      const token = getToken();
      
      if (isExcel) {
        // Handle Excel file - send to transcript processing pipeline
        const formData = new FormData();
        formData.append('file', file);
        
        const res = await axios.post(
          `${BACKEND_URL}/api/principal/upload-transcripts?token=${token}`,
          formData,
          { 
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 120000 // 2 minute timeout for large files
          }
        );
        
        const result = res.data;
        if (result.success) {
          toast({ 
            title: "✅ Transcripts Processed!", 
            description: `${result.summary.success} of ${result.summary.total} transcripts processed successfully. ${result.summary.sessions_created} sessions created.`,
            duration: 10000
          });
          
          if (result.errors && result.errors.length > 0) {
            console.warn('Import warnings:', result.errors);
            toast({ 
              title: "⚠️ Some records skipped", 
              description: result.errors.slice(0, 3).join(', '),
              variant: "warning",
              duration: 8000
            });
          }
        } else {
          toast({ 
            title: "Error", 
            description: result.detail || "Failed to process transcripts", 
            variant: "destructive" 
          });
        }
        
        loadData();
        return;
      }
      
      // Handle CSV file (existing logic)
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast({ title: "Error", description: "CSV file is empty or has no data rows", variant: "destructive" });
        return;
      }
      
      const headers = parseCSVLine(lines[0]);
      const sessions = [];
      
      for (let i = 1; i < lines.length; i++) {
        if (!lines[i].trim()) continue;
        const values = parseCSVLine(lines[i]);
        const session = {};
        headers.forEach((h, idx) => {
          session[h.trim()] = values[idx]?.trim() || '';
        });
        if (session.action) sessions.push(session);
      }
      
      const res = await axios.post(
        `${BACKEND_URL}/api/principal/sessions/import`,
        { sessions },
        { params: { token } }
      );
      
      const results = res.data.results;
      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;
      
      toast({ 
        title: "Import Complete", 
        description: `${successCount} operations successful, ${failCount} failed` 
      });
      
      loadData();
    } catch (error) {
      console.error('Import error:', error);
      toast({ title: "Error", description: "Failed to import sessions: " + (error.response?.data?.detail || error.message), variant: "destructive" });
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  }, [loadData, toast, parseCSVLine, getToken]);

  // Save session edit
  const saveEdit = useCallback(async (sessionId, editForm) => {
    try {
      const token = getToken();
      await axios.put(`${BACKEND_URL}/api/principal/session/${sessionId}`, null, {
        params: {
          token,
          session_date: editForm.sessionDate,
          started_at: `${editForm.sessionDate}T${editForm.startedAt}:00`,
          ended_at: `${editForm.sessionDate}T${editForm.endedAt}:00`,
          duration_minutes: editForm.durationMinutes,
          earnings: editForm.earnings,
          status: editForm.status,
          notes: editForm.notes,
          child_id: editForm.childId,
          observer_id: editForm.observerId
        }
      });
      
      toast({ title: "Success", description: "Session updated successfully" });
      loadData();
      return true;
    } catch (error) {
      console.error('Save error:', error);
      toast({ title: "Error", description: "Failed to update session", variant: "destructive" });
      return false;
    }
  }, [loadData, toast, getToken]);

  // Delete session
  const deleteSession = useCallback(async (sessionId, reason) => {
    if (!reason) return false;
    
    try {
      const token = getToken();
      await axios.delete(`${BACKEND_URL}/api/principal/session/${sessionId}`, {
        params: { token, reason }
      });
      
      toast({ title: "Success", description: "Session marked as deleted" });
      loadData();
      return true;
    } catch (error) {
      console.error('Delete error:', error);
      toast({ title: "Error", description: "Failed to delete session", variant: "destructive" });
      return false;
    }
  }, [loadData, toast, getToken]);

  // Add new session
  const addSession = useCallback(async (addForm) => {
    try {
      const token = getToken();
      await axios.post(`${BACKEND_URL}/api/principal/session`, null, {
        params: {
          token,
          child_id: addForm.childId,
          observer_id: addForm.observerId,
          session_date: addForm.sessionDate,
          started_at: `${addForm.sessionDate}T${addForm.startedAt}:00`,
          ended_at: `${addForm.sessionDate}T${addForm.endedAt}:00`,
          duration_minutes: addForm.durationMinutes,
          earnings: addForm.earnings,
          notes: addForm.notes,
          transcript: addForm.transcript,
          mood_rating: addForm.moodRating,
          topics_discussed: addForm.topicsDiscussed,
          key_observations: addForm.keyObservations,
          concerns: addForm.concerns
        }
      });
      
      toast({ title: "Success", description: "Session added successfully" });
      loadData();
      return true;
    } catch (error) {
      console.error('Add error:', error);
      toast({ title: "Error", description: "Failed to add session: " + (error.response?.data?.detail || error.message), variant: "destructive" });
      return false;
    }
  }, [loadData, toast, getToken]);

  return {
    // State
    loading,
    sessions,
    summary,
    filters,
    filterOptions,
    page,
    totalPages,
    total,
    exporting,
    importing,
    principalName,
    // Actions
    setFilters,
    setPage,
    loadData,
    clearFilters,
    exportSessions,
    handleFileUpload,
    saveEdit,
    deleteSession,
    addSession
  };
};

export default useSessionHistory;
